import _ from 'lodash'
import type { Structure, DeepStructure, ChildrenKey, SlotsData } from '@wix/thunderbolt-becky-types'
import { childrenKeys, pointers } from '@wix/thunderbolt-becky-types'

const { MOBILE_COMPONENTS, MASTER_PAGE_CHILDREN, COMPONENTS } = childrenKeys
const { PARENT, SLOTS_QUERY } = pointers.components.PROPERTY_TYPES

export const getChildrenKey = (component: DeepStructure, isMobile: boolean): ChildrenKey => {
	if (isMobile && component.mobileComponents) {
		return MOBILE_COMPONENTS
	}

	return component.children ? MASTER_PAGE_CHILDREN : COMPONENTS
}

const getChildrenComponents = (compStructure: DeepStructure, isMobile: boolean): Array<DeepStructure> => {
	const childrenKey = getChildrenKey(compStructure, isMobile)
	return compStructure[childrenKey] || []
}

const getAllCompsInStructure = (structure: DeepStructure, isMobile: boolean): Record<string, DeepStructure> => {
	const queue = [structure]
	for (const component of queue) {
		const childrenData = getChildrenComponents(component, isMobile)
		queue.push(...childrenData)
	}
	return queue.reduce((acc, val) => Object.assign(acc, { [val.id]: val }), {})
}

const convertChildrenToIds = (
	comp: DeepStructure,
	isMobile: boolean,
	slots: { [compId: string]: boolean },
	pageId: string,
	dontFilterSlots: boolean
) => {
	const metaData = { pageId }
	const newComp = {
		..._.omit(comp, [MASTER_PAGE_CHILDREN, MOBILE_COMPONENTS]),
		metaData,
	}
	const compChildren = comp[getChildrenKey(comp, isMobile)]
	return compChildren
		? {
			...newComp,
			components: dontFilterSlots ? compChildren.map((item) => item.id) : compChildren.map((item) => item.id).filter((id) => !slots[id]),
			metaData,
		}
		: newComp
}

export const flattenStructure = (
	pageStructure: DeepStructure,
	isMobile: boolean,
	slotsData: SlotsData,
	dontFilterSlots: boolean
): Structure => {
	const slots = _(slotsData)
		.flatMap((data) => _.values(data.slots))
		.mapKeys((compId) => compId)
		.mapValues(() => true)
		.value()

	const flatStructureWithComponents = getAllCompsInStructure(pageStructure, isMobile)
	const flatStructureWithCompIds = Object.keys(flatStructureWithComponents).reduce(
		(acc, id) =>
			Object.assign(acc, {
				[id]: convertChildrenToIds(flatStructureWithComponents[id], isMobile, slots, pageStructure.id, dontFilterSlots),
			}),
		{}
	)
	const flatStructure: Structure = _.mapValues(flatStructureWithCompIds, (comp) =>
		_.mapValues(comp, (v) => (_.isString(v) ? v.replace('#', '') : v))
	)

	// add a parent property to components
	_.forEach(flatStructure, (component) => {
		component.components?.forEach((childCompId: string) => {
			flatStructure[childCompId][PARENT] = component.id
		})

		const slotQuery = component[SLOTS_QUERY]
		if (slotQuery) {
			_(slotsData[slotQuery]?.slots)
				.values()
				.forEach((childCompId: string) => {
					flatStructure[childCompId][PARENT] = component.id
				})
		}
	})

	return flatStructure
}
